/* @jsxImportSource @emotion/react */
/* eslint-disable formatjs/no-literal-string-in-jsx */

import type React from 'react'
import { css } from '@emotion/react'

import { SliteLogo } from '@slite/components'
import { CloseIcon } from '@slite/icons'

import slackAvatar from './assets/slack-avatar.png'
import { TrafficDots } from './TrafficDots'

const SLACK_FONT =
  "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

const rootCss = css`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #1a1d21;
  font-family: ${SLACK_FONT};
  font-size: 13px;
  color: #1d1c1d;
  border: 4px solid #1A1D21;
  border-radius: 16px;
  overflow: hidden;
`

const AVATAR_SIZE = 36

function EllieAvatar() {
  return (
    <img
      src={slackAvatar}
      alt=""
      className="flexbox-no-shrink"
      style={{
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        borderRadius: 8,
        objectFit: 'cover',
      }}
    />
  )
}

function SliteBotAvatar() {
  return (
    <span
      className="flexbox flexbox-centered flexbox-no-shrink"
      style={{
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        borderRadius: 8,
        background: '#2F2F30',
      }}
    >
      <SliteLogo variant="symbol" style={{ width: 36, height: 36 }} />
    </span>
  )
}

function AddReactionIcon({ size = 15 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="9" cy="11" r="6.4" stroke="#616061" strokeWidth="1.7" />
      <circle cx="6.8" cy="9.8" r="1.1" fill="#616061" />
      <circle cx="11.2" cy="9.8" r="1.1" fill="#616061" />
      <path d="M6.2 12.6h5.6a2.8 2.8 0 0 1-5.6 0z" fill="#616061" />
      <path
        d="M15.7 2.3v6M12.7 5.3h6"
        stroke="#616061"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  )
}

function MessageMeta({ author, time }: { author: string; time: string }) {
  return (
    <div className="flexbox flexbox-align-baseline flexbox-gap-6">
      <span style={{ fontWeight: 900 }}>{author}</span>
      <span style={{ fontSize: 11, color: '#616061' }}>{time}</span>
    </div>
  )
}

function Message({
  avatar,
  author,
  time,
  children,
}: {
  avatar: React.ReactNode
  author: string
  time: string
  children: React.ReactNode
}) {
  return (
    <div className="flexbox" style={{ gap: 10, padding: '10px 14px' }}>
      {avatar}
      <div style={{ minWidth: 0 }}>
        <MessageMeta author={author} time={time} />
        {children}
      </div>
    </div>
  )
}

export function SlackInterface() {
  return (
    <div css={rootCss} aria-hidden>
      <div
        className="flexbox flexbox-align-center flexbox-no-shrink"
        style={{ gap: 10, height: 38, padding: '0 14px', marginTop: -4 }}
      >
        <TrafficDots color="#53575C" />
        <span style={{ fontSize: 12, color: '#B8BBBE' }}>Slack</span>
      </div>
      <div
        className="flexbox-grow"
        style={{
          margin: '0',
          background: '#FFF',
          borderRadius: '8px 8px 12px 12px',
          overflow: 'hidden',
        }}
      >
        <div
          className="flexbox flexbox-align-center flexbox-space-between"
          style={{
            padding: '12px 14px 8px',
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 14 }}>Thread</span>
          <CloseIcon size={14} style={{ color: '#616061' }} />
        </div>
        <Message avatar={<EllieAvatar />} author="Ellie" time="11:15 AM">
          <p style={{ lineHeight: 1.45, marginTop: 2 }}>
            <span
              style={{
                color: '#1264A3',
                background: '#E8F5FA',
                borderRadius: 3,
                padding: '0 2px',
              }}
            >
              @Slite
            </span>{' '}
            activation dropped since the new onboarding launched. Should we roll
            it back?
          </p>
          <div
            className="flexbox flexbox-align-center flexbox-gap-6"
            style={{ marginTop: 8 }}
          >
            <span
              className="flexbox flexbox-align-center flexbox-gap-4"
              style={{
                border: '1px solid #1D9BD1',
                background: '#EFF8FC',
                borderRadius: 12,
                padding: '1px 8px',
                fontSize: 11,
                color: '#1264A3',
              }}
            >
              ⁉️ 2
            </span>
            <span
              className="flexbox flexbox-centered"
              style={{
                background: '#F4F4F4',
                borderRadius: 12,
                padding: '3px 10px',
              }}
            >
              <AddReactionIcon />
            </span>
          </div>
        </Message>
        <div
          className="flexbox flexbox-align-center flexbox-gap-8"
          style={{ padding: '2px 14px' }}
        >
          <span style={{ fontSize: 11.5, color: '#616061', flexShrink: 0 }}>
            3 replies
          </span>
          <span
            className="flexbox-grow"
            style={{ height: 1, background: '#E2E2E2' }}
          />
        </div>
        <Message avatar={<SliteBotAvatar />} author="Slite" time="11:15 AM">
          <div
            style={{
              marginTop: 4,
              fontSize: 11.5,
              color: '#9B9B9B',
              lineHeight: 1.7,
              whiteSpace: 'nowrap',
            }}
          >
            <div>✓ Review the onboarding experiment brief</div>
            <div>✓ Compare the intended audience with the shipped code</div>
            <div>✓ Find related Linear bugs and support conversations</div>
          </div>
        </Message>
      </div>
    </div>
  )
}
